import { useEffect, useState } from "react";
import type { Canon, Category, DifficultyFilter } from "@shared/types";
import {
  ALL_CATEGORIES,
  CATEGORY_LABELS,
  DIFFICULTY_FILTER_LABELS,
  GAME,
} from "@shared/config";
import {
  createRoom,
  joinRoom,
  questionCount,
  recordRaceStart,
  type RoomSession,
} from "../lib/api";
import { getActiveProfile, type LocalProfile } from "../lib/profile";
import ProfileChip from "../components/ProfileChip";
import ProfileChooser from "../components/ProfileChooser";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

const DIFFICULTIES: DifficultyFilter[] = ["mixed", "easy", "medium", "hard"];
const CANONS: { value: Canon; label: string }[] = [
  { value: "protestant-66", label: "Protestant (66)" },
  { value: "catholic-73", label: "Catholic (73)" },
  { value: "orthodox", label: "Orthodox" },
];

export default function RoomEntry({
  onEnter,
  onBack,
}: {
  onEnter: (s: RoomSession) => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<"create" | "join">("create");
  const [nickname, setNickname] = useState("");
  const [active, setActive] = useState<LocalProfile | null>(() => getActiveProfile());
  const [chooserOpen, setChooserOpen] = useState(false);
  const [code, setCode] = useState("");
  const [difficulty, setDifficulty] = useState<DifficultyFilter>("mixed");
  const [categories, setCategories] = useState<Category[]>([]);
  const [canon, setCanon] = useState<Canon>("protestant-66");
  const [finishLine, setFinishLine] = useState<number>(GAME.roomDefaultFinishLine);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const noMatches = mode === "create" && matchCount === 0;

  function toggleCategory(c: Category) {
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  }

  // Live count of questions matching the room filters, so the host sees the
  // race length before creating. Only relevant in "create" mode; debounced and
  // ignores stale responses.
  useEffect(() => {
    if (mode !== "create") return;
    let active = true;
    setMatchCount(null);
    const t = window.setTimeout(() => {
      questionCount({ categories, difficulty, canon })
        .then((r) => {
          if (active) setMatchCount(r.count);
        })
        .catch(() => {
          if (active) setMatchCount(null);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(t);
    };
  }, [mode, categories, difficulty, canon]);

  async function submit() {
    setError("");
    // Identity comes from the active profile (consistent with the rest of the
    // app); the free-text field is only the guest fallback when none is active.
    const name = (active ? active.displayName : nickname).trim();
    if (!name) {
      setError("Pick or name your racer first!");
      return;
    }
    setBusy(true);
    try {
      if (mode === "create") {
        if (noMatches) {
          setError("No questions match those filters — pick more categories.");
          setBusy(false);
          return;
        }
        const s = await createRoom({
          nickname: name,
          categories,
          difficulty,
          canon,
          finishLine,
        });
        // "Start" for a private room means getting into one (created or joined);
        // the lobby wait for friends is outside this measure.
        recordRaceStart("room");
        onEnter(s);
      } else {
        const c = code.trim().toUpperCase();
        if (c.length < 4) {
          setError("Enter the room code your friend shared.");
          setBusy(false);
          return;
        }
        const s = await joinRoom({ code: c, nickname: name });
        recordRaceStart("room");
        onEnter(s);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={false} />
      <main className="mx-auto w-full max-w-lg px-4 pb-16 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={onBack}>
            ← Home
          </button>
        </div>

        <div className="mb-4 flex justify-center">
          <Wordmark width={260} />
        </div>

        <div className="panel p-5 sm:p-7">
          <h2 className="text-center font-display text-2xl text-ink sm:text-3xl">
            👥 Race Friends
          </h2>

          {/* mode toggle */}
          <div className="mt-4 grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => setMode("create")}
              aria-pressed={mode === "create"}
              className={`btn-chunk ${mode === "create" ? "bg-grass text-white" : "bg-white"}`}
            >
              Create room
            </button>
            <button
              type="button"
              onClick={() => setMode("join")}
              aria-pressed={mode === "join"}
              className={`btn-chunk ${mode === "join" ? "bg-grape text-white" : "bg-white"}`}
            >
              Join room
            </button>
          </div>

          {/* racer — same picker as the rest of the app (active profile + switch) */}
          <div className="mt-5">
            <span className="mb-1.5 block text-sm font-bold uppercase tracking-wide text-ink/70">
              Your racer
            </span>
            <ProfileChip profile={active} onClick={() => setChooserOpen(true)} />
            {!active && (
              <input
                type="text"
                value={nickname}
                maxLength={16}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="e.g. SpeedyNoah"
                autoComplete="off"
                className="mt-2 w-full rounded-2xl border-[4px] border-ink bg-white px-4 py-3 text-lg font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
              />
            )}
          </div>

          {mode === "create" ? (
            <div className="mt-5 space-y-5">
              <div>
                <div className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/70">
                  How tricky?
                </div>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                  {DIFFICULTIES.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDifficulty(d)}
                      aria-pressed={difficulty === d}
                      className={`btn-chunk text-sm ${
                        difficulty === d ? "bg-flame text-white" : "bg-white"
                      }`}
                    >
                      {DIFFICULTY_FILTER_LABELS[d]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-bold uppercase tracking-wide text-ink/70">
                    What to quiz on?
                  </span>
                  <span className="text-xs font-semibold text-ink/45">
                    {categories.length === 0
                      ? "Everything"
                      : `${categories.length} picked`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {ALL_CATEGORIES.map((c) => {
                    const active = categories.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCategory(c)}
                        aria-pressed={active}
                        className={`chip-chunk ${active ? "bg-grass text-white" : "bg-white"}`}
                      >
                        {CATEGORY_LABELS[c]}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 min-h-[1.25rem] text-xs font-semibold">
                  {matchCount === null ? (
                    <span className="text-ink/40">Counting questions…</span>
                  ) : noMatches ? (
                    <span className="text-flame">
                      No questions match — pick more categories.
                    </span>
                  ) : (
                    <span className="text-ink/60">
                      🏁 {matchCount} question{matchCount === 1 ? "" : "s"} in this mix ·
                      race to {Math.min(finishLine, matchCount)} correct
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="rlfinish"
                  className="mb-2 flex items-center justify-between text-sm font-bold uppercase tracking-wide text-ink/70"
                >
                  <span>Finish line</span>
                  <span className="tabular-nums text-flame">{finishLine} correct</span>
                </label>
                <input
                  id="rlfinish"
                  type="range"
                  min={GAME.minFinishLine}
                  max={GAME.maxFinishLine}
                  step={1}
                  value={finishLine}
                  onChange={(e) => setFinishLine(Number(e.target.value))}
                  className="w-full accent-flame"
                />
              </div>

              {/* advanced: canon (mirrors solo) */}
              <div className="border-t-[3px] border-dashed border-ink/15 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced((s) => !s)}
                  className="flex w-full items-center justify-between text-sm font-bold text-ink/70"
                  aria-expanded={showAdvanced}
                >
                  <span>Grown-up options</span>
                  <span aria-hidden>{showAdvanced ? "▲" : "▼"}</span>
                </button>
                {showAdvanced && (
                  <div className="mt-4">
                    <div className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/70">
                      Canon
                    </div>
                    <div className="grid grid-cols-3 gap-2.5">
                      {CANONS.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setCanon(c.value)}
                          aria-pressed={canon === c.value}
                          className={`btn-chunk text-xs sm:text-sm ${
                            canon === c.value ? "bg-grape text-white" : "bg-white"
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-ink/50">
                      Protestant (66) hides Apocrypha-dependent questions. Wider
                      canons include them.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <label className="mt-5 block">
              <span className="mb-1.5 block text-sm font-bold uppercase tracking-wide text-ink/70">
                Room code
              </span>
              <input
                type="text"
                value={code}
                maxLength={6}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full rounded-2xl border-[4px] border-ink bg-white px-4 py-3 text-center font-display text-3xl tracking-[0.3em] text-ink outline-none placeholder:text-ink/25 focus:border-flame"
              />
            </label>
          )}

          {error && (
            <p className="mt-4 rounded-xl border-[3px] border-flame bg-flame/10 px-3 py-2 text-center text-sm font-bold text-flame">
              {error}
            </p>
          )}

          <button
            type="button"
            className="btn-play mt-6 w-full"
            onClick={submit}
            disabled={busy || noMatches}
          >
            {busy ? "…" : mode === "create" ? "CREATE ROOM" : "JOIN ROOM"}
            <span className="sheen animate-shine" aria-hidden />
          </button>
        </div>

        <p className="mt-5 text-center text-xs font-medium text-ink/50">
          Everyone races the same questions at the same time. First to the finish
          line wins! 🏁
        </p>
      </main>

      {chooserOpen && (
        <ProfileChooser
          onClose={() => setChooserOpen(false)}
          onChange={(p) => setActive(p)}
        />
      )}
    </div>
  );
}
